import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tooltip } from 'primereact/tooltip';
import { Ripple } from 'primereact/ripple';
import { sendChatMessageAPI } from '../../WebApp/Api/WebsiteServices/websiteService';

type ChatRole = 'assistant' | 'user' | 'system';

export interface VisitorDetails {
    name: string;
    email: string;
    phone: string;
    updatedAt: number;
}

export interface ChatMessage {
    id: string;
    role: ChatRole;
    text: string;
    createdAt: number;
}

export interface ChatWidgetProps {
    onSend?: (payload: { text: string; createdAt: number; visitor?: VisitorDetails }) => Promise<void> | void;
}

const initialGreeting: ChatMessage = {
    id: 'greeting-1',
    role: 'assistant',
    text: 'Hi 👋 How can we help you today?',
    createdAt: Date.now(),
};

const LOCAL_STORAGE_KEY = 'traqdo_chat_visitor';

function validateName(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return "Name is required";
    if (trimmed.length < 2) return "Name must be at least 2 characters";
    if (/^\d+$/.test(trimmed)) return "Name cannot be only numbers";
    return "";
}

function validateEmail(email: string) {
    if (!email.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Invalid email format";
    return "";
}

function validatePhone(phone: string) {
    if (!phone.trim()) return "Phone is required";
    const digits = phone.replace(/[^\d+]/g, '');
    if (digits.length < 9 || digits.length > 20) return "Phone must be between 9 and 15 digits";
    return "";
}

export default function ChatWidget({ onSend }: ChatWidgetProps) {
    const logo = "/assets/images/traqdo_logo_icon_light.svg";
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [messages, setMessages] = useState<ChatMessage[]>([initialGreeting]);
    const [input, setInput] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const [chatStep, setChatStep] = useState<'visitor' | 'chat'>('visitor');
    const [visitor, setVisitor] = useState<VisitorDetails | null>(null);
    const [visitorInput, setVisitorInput] = useState({ name: '', email: '', phone: '' });
    const [visitorErrors, setVisitorErrors] = useState({ name: '', email: '', phone: '' });

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const visitorNameInputRef = useRef<HTMLInputElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    };

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (chatStep === 'chat') {
                    scrollToBottom();
                    inputRef.current?.focus();
                } else {
                    visitorNameInputRef.current?.focus();
                }
            }, 300);
        }
    }, [isOpen, chatStep]);

    useEffect(() => {
        const timer = setTimeout(scrollToBottom, 50);
        return () => clearTimeout(timer);
    }, [messages, isSending, error]);

    useEffect(() => {
        const handleKeyDown = (e: any) => {
            if (e.key === 'Escape' && isOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const toggleOpen = () => {
        if (!isOpen) {
            try {
                const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed && parsed.name && parsed.email && parsed.phone) {
                        setVisitor(parsed);
                        setChatStep('chat');
                        setMessages(prev => {
                            if (prev.length === 1 && prev[0].id === 'greeting-1') {
                                return [{
                                    ...prev[0],
                                    text: `Hi 👋 ${parsed.name.split(' ')[0]}, How can we help you today?`
                                }];
                            }
                            return prev;
                        });
                    } else {
                        setChatStep('visitor');
                    }
                } else {
                    setChatStep('visitor');
                }
            } catch (e) {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                setChatStep('visitor');
            }
        }
        setIsOpen(!isOpen);
        setError(null);
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || !visitor) return;

        setInput('');
        setIsSending(true);
        setError(null);

        try {
            const data = await sendChatMessageAPI(
                visitor.name,
                visitor.phone,
                visitor.email,
                text,
            );

            if (data?.statusCode === 200) {
                const newUserMsg: ChatMessage = {
                    id: Date.now().toString(),
                    role: 'user',
                    text,
                    createdAt: Date.now(),
                };
                const ackMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    text: 'Thank you for reaching out to us. We have received your message and will get back to you within 48 hours.',
                    createdAt: Date.now(),
                };

                setMessages((prev) => [...prev, newUserMsg, ackMsg]);
            } else {
                setError('Message failed to send. Please try again.');
                setInput(text);
            }
        } catch (err: unknown) {
            setError('Message failed to send. Please try again.');
            setInput(text);
        } finally {
            setIsSending(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const onInputKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const autoResizeInput = () => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
        }
    };

    useEffect(() => {
        autoResizeInput();
    }, [input]);

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    };

    const handleVisitorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setVisitorInput(prev => ({ ...prev, [name]: value }));

        let errorMsg = '';
        if (name === 'name') errorMsg = validateName(value);
        if (name === 'email') errorMsg = validateEmail(value);
        if (name === 'phone') errorMsg = validatePhone(value);

        setVisitorErrors(prev => ({ ...prev, [name]: errorMsg }));
    };

    const handleVisitorSubmit = () => {
        const nameErr = validateName(visitorInput.name);
        const emailErr = validateEmail(visitorInput.email);
        const phoneErr = validatePhone(visitorInput.phone);

        if (nameErr || emailErr || phoneErr) {
            setVisitorErrors({ name: nameErr, email: emailErr, phone: phoneErr });
            return;
        }

        const newVisitor: VisitorDetails = {
            name: visitorInput.name.trim(),
            email: visitorInput.email.trim(),
            phone: visitorInput.phone.trim(),
            updatedAt: Date.now()
        };

        const isChanged = !visitor ||
            visitor.name !== newVisitor.name ||
            visitor.email !== newVisitor.email ||
            visitor.phone !== newVisitor.phone;

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newVisitor));
        setVisitor(newVisitor);

        if (isChanged) {
            setMessages([{
                ...initialGreeting,
                id: 'greeting-' + Date.now().toString(),
                text: `Hi 👋 ${newVisitor.name.split(' ')[0]}, How can we help you today?`
            }]);
            setInput('');
        }

        setChatStep('chat');
    };

    const handleVisitorKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleVisitorSubmit();
        }
    };

    const startEditingVisitor = () => {
        if (visitor) {
            setVisitorInput({ name: visitor.name, email: visitor.email, phone: visitor.phone });
            setVisitorErrors({ name: '', email: '', phone: '' });
        }
        setChatStep('visitor');
    };

    const isVisitorValid = !validateName(visitorInput.name) && !validateEmail(visitorInput.email) && !validatePhone(visitorInput.phone);

    const hasUnchangedDetails = visitor !== null &&
        visitor.name === visitorInput.name.trim() &&
        visitor.email === visitorInput.email.trim() &&
        visitor.phone === visitorInput.phone.trim();

    return (
        <>
            <Tooltip target=".cw-fab-tooltip" position="left" />

            <div className="cw-container position-fixed d-flex flex-column align-items-end">
                <AnimatePresence>
                    {isOpen && (
                        <motion.div
                            className="cw-panel shadow-lg"
                            initial={{ opacity: 0, y: 20, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.95 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                        >
                            <div className="cw-header d-flex justify-content-between align-items-center px-3 py-3 rounded-top-3">
                                <div className="d-flex align-items-center gap-2">
                                    <div className="cw-avatar d-flex align-items-center justify-content-center rounded-circle">
                                        <img src={logo} alt="Logo" className="img-fluid" />
                                    </div>
                                    <div>
                                        <h6 className="text-white cw-header-title">Traqdo Support</h6>
                                        <div className="d-flex align-items-center gap-2">
                                            <small className="text-white-50 d-flex align-items-center gap-1 cw-header-subtitle">
                                                <span className="cw-online-dot rounded-circle bg-success"></span>
                                                Here when you need us.
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-sm text-white border-0 p-1"
                                    onClick={toggleOpen}
                                    aria-label="Close chat"
                                >
                                    <i className="bi bi-x-lg fs-5 opacity-75 cw-close-icon"></i>
                                </button>
                            </div>

                            {chatStep === 'visitor' ? (
                                <motion.div
                                    className="cw-visitor-view"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="cw-visitor-header">
                                        <div className="cw-visitor-icon-container">
                                            <i className="bi bi-chat-dots-fill"></i>
                                        </div>
                                        <h5 className="cw-visitor-title">Welcome!</h5>
                                        <p className="cw-visitor-subtitle">
                                            Please enter your details below so we can assist you better.
                                        </p>
                                    </div>

                                    <div className="cw-visitor-form">
                                        <div className="cw-visitor-field">
                                            <label className="cw-visitor-label">Full Name</label>
                                            <div className="cw-input-wrapper">
                                                <i className="bi bi-person cw-input-icon"></i>
                                                <input
                                                    ref={visitorNameInputRef}
                                                    type="text"
                                                    name="name"
                                                    value={visitorInput.name}
                                                    onChange={handleVisitorChange}
                                                    onKeyDown={handleVisitorKeyDown}
                                                    className={`cw-visitor-input ${visitorErrors.name ? 'is-invalid' : ''}`}
                                                    placeholder="John Doe"
                                                />
                                            </div>
                                            <AnimatePresence>
                                                {visitorErrors.name && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="cw-visitor-input-error-container">
                                                        <span className="cw-visitor-input-error"><i className="bi bi-exclamation-circle"></i>{visitorErrors.name}</span>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        <div className="cw-visitor-field">
                                            <label className="cw-visitor-label">Email Address</label>
                                            <div className="cw-input-wrapper">
                                                <i className="bi bi-envelope cw-input-icon"></i>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={visitorInput.email}
                                                    onChange={handleVisitorChange}
                                                    onKeyDown={handleVisitorKeyDown}
                                                    className={`cw-visitor-input ${visitorErrors.email ? 'is-invalid' : ''}`}
                                                    placeholder="john@example.com"
                                                />
                                            </div>
                                            <AnimatePresence>
                                                {visitorErrors.email && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="cw-visitor-input-error-container">
                                                        <span className="cw-visitor-input-error"><i className="bi bi-exclamation-circle"></i>{visitorErrors.email}</span>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        <div className="cw-visitor-field">
                                            <label className="cw-visitor-label">Phone Number</label>
                                            <div className="cw-input-wrapper">
                                                <i className="bi bi-telephone cw-input-icon"></i>
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={visitorInput.phone}
                                                    onChange={handleVisitorChange}
                                                    onKeyDown={handleVisitorKeyDown}
                                                    className={`cw-visitor-input ${visitorErrors.phone ? 'is-invalid' : ''}`}
                                                    placeholder="+1234567890"
                                                />
                                            </div>
                                            <AnimatePresence>
                                                {visitorErrors.phone && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="cw-visitor-input-error-container">
                                                        <span className="cw-visitor-input-error"><i className="bi bi-exclamation-circle"></i>{visitorErrors.phone}</span>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>

                                        <div className="cw-visitor-actions">
                                            <button
                                                className="cw-btn-primary"
                                                onClick={handleVisitorSubmit}
                                                disabled={!isVisitorValid || !visitorInput.name || !visitorInput.email || !visitorInput.phone}
                                            >
                                                <span>{hasUnchangedDetails ? 'Continue' : 'Start Chat'}</span>
                                                <i className="bi bi-arrow-right"></i>
                                                <Ripple />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div
                                    className="cw-chat-view"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="cw-body">
                                        <div className="cw-chat-info">
                                            <p><i className="bi bi-clock cw-chat-info-icon"></i><strong>Typical reply time:</strong>&nbsp;within 6-12 hrs</p>
                                            <p><i className="bi bi-calendar-check cw-chat-info-icon"></i>Available Mon–Fri, 9:00 AM–5:00 PM</p>
                                        </div>

                                        {visitor && (
                                            <div className="cw-visitor-summary">
                                                <div className="cw-visitor-summary-header">
                                                    <span>Your Details</span>
                                                    <button onClick={startEditingVisitor} aria-label="Edit details" title="Edit details">
                                                        <i className="bi bi-pencil-square"></i>
                                                    </button>
                                                </div>
                                                <div className="cw-visitor-summary-body">
                                                    <div><i className="bi bi-person"></i> {visitor.name}</div>
                                                    <div><i className="bi bi-envelope"></i> {visitor.email}</div>
                                                    <div><i className="bi bi-telephone"></i> {visitor.phone}</div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="cw-messages">
                                            {messages.map((msg) => (
                                                <motion.div
                                                    key={msg.id}
                                                    initial={{ opacity: 0, y: 12 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className={`cw-message-row ${msg.role === 'user' ? 'cw-message-row--user' : 'cw-message-row--assistant'}`}
                                                >
                                                    {msg.role === 'assistant' && (
                                                        <div className="cw-message-avatar">
                                                            <img src={logo} alt="Bot" />
                                                        </div>
                                                    )}
                                                    <div className={`cw-bubble ${msg.role === 'user' ? 'cw-bubble--user' : 'cw-bubble--assistant'}`}>
                                                        {msg.text}
                                                    </div>
                                                </motion.div>
                                            ))}

                                            <AnimatePresence>
                                                {isSending && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 12 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        exit={{ opacity: 0, scale: 0.9, originY: 1 }}
                                                        className="cw-message-row cw-message-row--user"
                                                    >
                                                        <div className="cw-bubble cw-bubble--user cw-bubble--sending">
                                                            <div className="cw-typing-indicator">
                                                                <span></span><span></span><span></span>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>

                                            <AnimatePresence>
                                                {error && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                        animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
                                                        exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                                                    >
                                                        <div className="cw-error-banner" role="alert">
                                                            <i className="bi bi-exclamation-triangle-fill"></i>
                                                            <span>{error}</span>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                            <div ref={messagesEndRef} className="cw-messages-bottom" />
                                        </div>
                                    </div>

                                    <div className="cw-composer">
                                        <div className="cw-composer-inner">
                                            <textarea
                                                ref={inputRef}
                                                className="cw-input"
                                                placeholder="Type your message..."
                                                rows={1}
                                                value={input}
                                                onChange={handleInputChange}
                                                onKeyDown={onInputKeyDown}
                                                disabled={isSending}
                                                aria-label="Message"
                                            ></textarea>
                                            <button
                                                className="cw-send-btn p-ripple"
                                                onClick={handleSend}
                                                disabled={!input.trim() || isSending}
                                                aria-label="Send message"
                                                title="Send message"
                                            >
                                                {isSending ? (
                                                    <div className="cw-spinner"></div>
                                                ) : (
                                                    <i className="bi bi-send-fill"></i>
                                                )}
                                                <Ripple />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.button
                    className="cw-fab cw-fab-tooltip shadow d-flex align-items-center justify-content-center p-0 mt-3"
                    data-pr-tooltip={isOpen ? "Close chat" : "Need help? Support chat"}
                    onClick={toggleOpen}
                    aria-label="Open chat"
                    aria-expanded={isOpen}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <AnimatePresence mode="wait">
                        {isOpen ? (
                            <motion.i
                                key="close"
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                className="bi bi-x-lg fs-4"
                            ></motion.i>
                        ) : (
                            <motion.i
                                key="chat"
                                initial={{ rotate: 90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: -90, opacity: 0 }}
                                className="bi bi-chat-dots-fill fs-4 position-relative"
                            >
                                <span className="cw-notification-dot">
                                    <span className="visually-hidden">New alerts</span>
                                </span>
                            </motion.i>
                        )}
                    </AnimatePresence>
                </motion.button>
            </div>
        </>
    );
}
